import React, { useState, useEffect } from "react"
import { toast } from "react-toastify"
import { UserQuota } from "../types/ai-dashboard"
import { SetUserQuotaPayload } from "../_requests"

interface QuotaModalProps {
     user: UserQuota | null
     onClose: () => void
     onSave: (userId: number, payload: SetUserQuotaPayload) => Promise<void>
}

const QuotaModal: React.FC<QuotaModalProps> = ({ user, onClose, onSave }) => {
     const [dailyLimit, setDailyLimit]     = useState<string>("")
     const [monthlyLimit, setMonthlyLimit] = useState<string>("")
     const [isUnlimited, setIsUnlimited]   = useState<boolean>(false)
     const [isSaving, setIsSaving]         = useState<boolean>(false)

     useEffect(() => {
          if (user) {
               setDailyLimit(user.dailyLimit != null ? String(user.dailyLimit) : "")
               setMonthlyLimit(user.monthlyLimit != null ? String(user.monthlyLimit) : "")
               setIsUnlimited(user.dailyLimit === null && user.monthlyLimit === null)
          }
     }, [user])

     useEffect(() => {
          const handleKeyDown = (e: KeyboardEvent) => {
               if (e.key === "Escape" && !isSaving) onClose()
          }
          document.addEventListener("keydown", handleKeyDown)
          return () => document.removeEventListener("keydown", handleKeyDown)
     }, [onClose, isSaving])

     if (!user) return null

     const handleSave = () => {
          const payload: SetUserQuotaPayload = isUnlimited
               ? { isUnlimited: true }
               : { isUnlimited: false, dailyLimit: Math.max(0, parseInt(dailyLimit) || 0), monthlyLimit: Math.max(0, parseInt(monthlyLimit) || 0) }

          setIsSaving(true)
          onSave(user.id, payload)
               .then(() => {
                    toast.success(`Quotas de ${user.name} mis à jour.`)
                    onClose()
               })
               .catch(e => {
                    toast.error(e?.response?.data?.detail ?? "Erreur lors de la sauvegarde.", { autoClose: false })
               })
               .finally(() => setIsSaving(false))
     }

     return (
          <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
               <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content border-0 shadow">
                         <div className="modal-header">
                              <h5 className="modal-title fw-bold">Paramètres Quotas : {user.name}</h5>
                              <button type="button" className="btn-close" onClick={onClose} disabled={isSaving} />
                         </div>
                         <div className="modal-body">
                              <div className="mb-3">
                                   <label className="form-label small fw-bold">Limite Journalière (tokens)</label>
                                   <input
                                        type="number"
                                        className="form-control"
                                        value={dailyLimit}
                                        onChange={e => setDailyLimit(e.target.value)}
                                        disabled={isUnlimited || isSaving}
                                   />
                                   <div className="form-text text-muted">Actuellement : {user.dailyUsed.toLocaleString()} consommés.</div>
                              </div>
                              <div className="mb-3">
                                   <label className="form-label small fw-bold">Limite Mensuelle (tokens)</label>
                                   <input
                                        type="number"
                                        className="form-control"
                                        value={monthlyLimit}
                                        onChange={e => setMonthlyLimit(e.target.value)}
                                        disabled={isUnlimited || isSaving}
                                   />
                              </div>
                              <div className="form-check form-switch mt-4">
                                   <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="unlimited"
                                        checked={isUnlimited}
                                        onChange={e => setIsUnlimited(e.target.checked)}
                                        disabled={isSaving}
                                   />
                                   <label className="form-check-label" htmlFor="unlimited">
                                        Accès Illimité (Attention au budget !)
                                   </label>
                              </div>
                         </div>
                         <div className="modal-footer bg-light">
                              <button className="btn btn-secondary" onClick={onClose} disabled={isSaving}>
                                   Annuler
                              </button>
                              <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                                   {isSaving ? (
                                        <>
                                             <span className="spinner-border spinner-border-sm me-2" role="status" />
                                             Sauvegarde...
                                        </>
                                   ) : "Sauvegarder les modifications"}
                              </button>
                         </div>
                    </div>
               </div>
          </div>
     )
}

export default QuotaModal
